import React, { useState, useEffect } from 'react';
import { message, Spin } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import SelectPropertyData from '../serviceComponents/selectPropertyData/SelectPropertyData';
import ServiceEstimate from '../serviceComponents/serviceEstimate/ServiceEstimate';
import FillDetails from '../serviceComponents/fillDetails/FillDetails';
import GeoMap from '../serviceComponents/geoMap/GeoMap';
import NewService from '../serviceComponents/newService/NewService';
import Summary from '../serviceComponents/summary/Summary';
import SuccessSolicitation from '../serviceComponents/successSolicitation/SuccessSolicitation';
import NewLand from '../serviceComponents/newLand/NewLand';
import {
  get, ResponseWrapper, RequestWrapper, isSuperAgentError,
} from '../../../dataSource/requests';
import { getUserType } from '../../../utils/users';

interface IParameters {
  isSkippingSteps: boolean;
  serviceList: [];
  haveCtc: boolean;
  ctc: string;
  branchCoopercitrusId: number;

  selectedService: {
    id: number;
    idCode: string;
    name: string;
    description: string;
    types: [];
  };

  selectedType: {
    id: number;
    idCode: string;
    name: string;
    subtypes: [];
  };

  selectedProperty: {
    id: string;
    name: string;

    location: {
      lat: number|null;
      lon: number|null;
    },

    registeredOn: string;
    matriculation: string;
  },

  selectedMatriculation: string|null;

  selectedOwner: {
    id: string;
    name: string;
  },

  serviceTypeDynamicFields: [];
  serviceSubtypeDynamicFields: [];
  talhoesSelecionados: [];
}

const initialParameters = (): IParameters => ({
  isSkippingSteps: false,
  serviceList: [],

  haveCtc: false,
  ctc: '',
  branchCoopercitrusId: 0,

  selectedService: {
    id: 0,
    idCode: '',
    name: '',
    description: '',
    types: [],
  },

  selectedType: {
    id: 0,
    idCode: '',
    name: '',
    subtypes: [],
  },

  selectedProperty: {
    id: '',
    name: '',

    location: {
      lat: null,
      lon: null,
    },

    registeredOn: '',
    matriculation: '',
  },

  selectedMatriculation: getUserType() === 'COOPERCITRUS' ? null : 'COOPERATIVE',

  selectedOwner: {
    id: '',
    name: '',
  },

  serviceTypeDynamicFields: [],
  serviceSubtypeDynamicFields: [],
  talhoesSelecionados: [],
});

const FlowService = (): JSX.Element => {
  const navigate = useNavigate();
  const locationObject = useLocation();
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [params, setParams] = useState<IParameters>(initialParameters());
  const [typeCurrentUser, setTypeCurrentUser] = useState<string|null>(null);

  useEffect(() => {
    const getAllServices = async (): Promise<void> => {
      setLoading(true);

      let response: ResponseWrapper | undefined;
      let responseError: ResponseWrapper | undefined;
      let genericError: Error | undefined;

      const requestReference: RequestWrapper = get({
        url: '/service/listAll',

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      try {
        response = await requestReference;
      } catch (error) {
        if (isSuperAgentError(error)) {
          responseError = error.response;
        } else {
          genericError = error as Error;
        }
      }

      setLoading(false);

      switch (true) {
        case (response?.body?.code === 5000): {
          // "Services listed successfully", 5000
          const { services } = response?.body;

          setParams((previous) => {
            return ({
              ...previous,
              serviceList: services,
            });
          });
        } break;

        case (responseError?.body?.code === -4001):
          // "You must be authenticated to perform this operation", -4001
          break;

        case (genericError?.message?.includes('has been aborted')):
          // dont show anything when request is aborted
          break;

        default:
          message.error('Ocorreu um erro ao buscar os serviços, tente novamente');
          break;
      }
    };

    setTypeCurrentUser(getUserType());
    getAllServices();

    return () => {
      setLoading(false);
    };
  }, []);

  useEffect(() => {
    const skipSteps = (): void => {
      if (
        !locationObject.state
        || !locationObject.state.skipToPage
        || !locationObject.state.serviceID
        || !locationObject.state.matriculation
      ) {
        return;
      }
      switch (locationObject.state.skipToPage) {
        case -1:
          // to-do
          break;

        case 0:
          // to-do
          break;

        case 1: {
          // orcamento ou servico, ctc, filial
          let newParameters = {
            ...params,
            isSkippingSteps: true,
          };

          if (locationObject.state.allServiceParameters) {
            newParameters = {
              ...params,
              ...locationObject.state.allServiceParameters,
            };
          }

          setParams(newParameters);
          setCurrentPage(locationObject.state.skipToPage);
        } break;

        case 2: {
          // select property data
          let newParameters = {
            ...params,
            isSkippingSteps: true,

            matriculation: {
              error: false,
              errorMessage: '',
              status: 'success',
              value: locationObject.state.matriculation,
            },

            selectedMatriculation: locationObject.state.matriculation,

            selectedService: params
              .serviceList
              .find(
                (serviceElement) => {
                  return serviceElement.id === locationObject.state.serviceID;
                },
              ),
          };

          if (locationObject.state.allServiceParameters) {
            newParameters = {
              ...params,
              ...locationObject.state.allServiceParameters,
            };
          }

          setParams(newParameters);
          setCurrentPage(locationObject.state.skipToPage);
        } break;

        case 3: {
          // selecionar talhoes
          let newParameters = {
            ...params,
            isSkippingSteps: true,
          };

          if (locationObject.state.allServiceParameters) {
            newParameters = {
              ...params,
              ...locationObject.state.allServiceParameters,
            };
          }

          setParams(newParameters);
          setCurrentPage(locationObject.state.skipToPage);
        } break;

        case 4:
          // to-do
          break;

        case 5:
          // to-do
          break;

        case 6:
          // to-do
          break;

        default:
          // to-do
          break;
      }
    };

    if (locationObject.state && params.serviceList.length && currentPage === 0) {
      skipSteps();
    }

    return () => {
      setLoading(false);
    };
  });

  useEffect(() => {
    switch (true) {
      case (
        currentPage >= 4
        && params.selectedService.idCode === 'GEOFERT'
        && params.selectedType.idCode === 'GEOFERT-COLETA_TALHOES'
      ):
        navigate(
          '/service/geofert/coletaportalhoes/new',
          {
            state: {
              serviceRequestParams: params,
            },
          },
        );
        break;

      case (
        currentPage >= 4
        && params.selectedService.idCode === 'GEOFERT'
        && params.selectedType.idCode === 'GEOFERT-CLASSIFICACAO_SOLO_AMBIENTE_PRODUCAO'
      ):
        navigate(
          '/service/geofert/classificacaosolo/new',
          {
            state: {
              serviceRequestParams: params,
            },
          },
        );
        break;

      case (
        currentPage >= 4
        && params.selectedService.idCode === 'GEOFERT'
        && params.selectedType.idCode === 'GEOFERT-GRID'
      ):
        navigate(
          '/service/geofert/grid/new',
          {
            state: {
              serviceRequestParams: params,
            },
          },
        );
        break;

      /*
  case (
    currentPage >= 4
    && params.selectedService.idCode === 'VANT'
    && params.selectedType.idCode === 'VANT-ORTOMOSAICO'
  ):
    navigate(
      '/service/vant/ortomosaico/new',
      {
        state: {
          serviceRequestParams: params,
        },
      },
    );
    break;

  case (
    currentPage >= 4
    && params.selectedService.idCode === 'VANT'
    && params.selectedType.idCode === 'VANT-SISTEMATIZACAO'
  ):
    navigate(
      '/service/vant/sistematizacao/new',
      {
        state: {
          serviceRequestParams: params,
        },
      },
    );
    break;

  case (
    currentPage >= 4
    && params.selectedService.idCode === 'VANT'
    && params.selectedType.idCode === 'VANT-PROJETO_PLANTIO'
  ):
    navigate(
      '/service/vant/projetoplantio/new',
      {
        state: {
          serviceRequestParams: params,
        },
      },
    );
    break;

  case (
    currentPage >= 4
    && params.selectedService.idCode === 'VANT'
    && params.selectedType.idCode === 'VANT-LEVANTAMENTO_FALHAS'
  ):
    navigate(
      '/service/vant/levantamentofalhas/new',
      {
        state: {
          serviceRequestParams: params,
        },
      },
    );
    break;

  case (
    currentPage >= 4
    && params.selectedService.idCode === 'VANT'
    && params.selectedType.idCode === 'VANT-MAPEAMENTO_LINHAS'
  ):
    navigate(
      '/service/vant/mapeamentolinhas/new',
      {
        state: {
          serviceRequestParams: params,
        },
      },
    );
    break;

  case (
    currentPage >= 4
    && params.selectedService.idCode === 'VANT'
    && params.selectedType.idCode === 'VANT-MAPEAMENTO_ERVAS_INVASORAS'
  ):
    navigate(
      '/service/vant/mapeamentoervasinvasoras/new',
      {
        state: {
          serviceRequestParams: params,
        },
      },
    );
    break;

  case (
    currentPage >= 4
    && params.selectedService.idCode === 'VANT'
    && params.selectedType.idCode === 'VANT-ZONA_MANEJO'
  ):
    navigate(
      '/service/vant/zonamanejo/new',
      {
        state: {
          serviceRequestParams: params,
        },
      },
    );
    break; */

      case (
        currentPage >= 4
        && params.selectedService.idCode === 'GEOFERT'
        && params.selectedType.idCode === 'GEOFERT-ZONA_MANEJO'
      ):
        navigate(
          '/service/geofert/zonaDeManejo/new',
          {
            state: {
              serviceRequestParams: params,
            },
          },
        );
        break;

      case (
        currentPage >= 4
        && params.selectedService.idCode === 'POS_VENDAS'
        && params.selectedType.idCode === 'POS_VENDAS-ASSISTENCIA_TECNICA'
      ):
        navigate(
          '/service/posVendas/assistenciaTecnica/new',
          {
            state: {
              serviceRequestParams: params,
            },
          },
        );
        break;

      case (
        currentPage >= 4
        && params.selectedService.idCode === 'POS_VENDAS'
        && params.selectedType.idCode === 'POS_VENDAS-LOCACAO_DE_PILOTO'
      ):
        navigate(
          '/service/posVendas/locacaoPiloto/new',
          {
            state: {
              serviceRequestParams: params,
            },
          },
        );
        break;

      case (
        currentPage >= 4
        && params.selectedService.idCode === 'POS_VENDAS'
        && params.selectedType.idCode === 'POS_VENDAS-MONTAGEM_DE_EQUIPAMENTO'
      ):
        navigate(
          '/service/posVendas/montagemEquipamento/new',
          {
            state: {
              serviceRequestParams: params,
            },
          },
        );
        break;

      default:
        break;
    }
  }, [currentPage]);

  const getTitleText = (currentPageParameter): string => {
    switch (currentPageParameter) {
      case 0:
        return 'Novo serviço';
      case 1:
        if (getUserType() === 'COOPERCITRUS') {
          return 'Novo serviço';
        }

        return 'Nova solicitação';
      case 2:
      case 3:
      case 4:
        if (params.selectedMatriculation === null) {
          return `Novo serviço: ${params.selectedService.name}`;
        }

        if (params.choiceServiceType === 1) {
          return `Novo orçamento: ${params.selectedService.name}`;
        }

        return `Novo serviço: ${params.selectedService.name}`;
      case 5:
        return 'Resumo da solicitação';
      case 6:
        return '';
      case -1:
        return 'Adicionar propriedade';
      default:
        return '';
    }
  };

  const previousPage = (parameters): void => {
    if (
      currentPage === 5
      && params.selectedService.idCode === 'VANT'
      && params.selectedType.idCode === 'VANT-ORTOMOSAICO'
    ) {
      setParams((previous) => {
        return ({
          ...previous,
          ...parameters,
        });
      });

      setCurrentPage(3);
      return;
    }

    if (currentPage === -1) {
      // quando estamos na pagina de inserir propriedade e clicamos em cancelar/voltar
      setParams((previous) => {
        return ({
          ...previous,
          ...parameters,
        });
      });

      setCurrentPage(2);
      return;
    }

    if (
      currentPage === 4
      && !params.serviceTypeDynamicFields.length
      && !params.serviceSubtypeDynamicFields.length
    ) {
      // quando estamos na pagina de inserir detalhes e clicamos em voltar
      setParams((previous) => {
        return ({
          ...previous,
          ...parameters,
        });
      });

      setCurrentPage((previous) => previous - 2);
      return;
    }

    if (params.selectedMatriculation === null && currentPage === 2) {
      setParams((previous) => {
        return ({
          ...previous,
          ...parameters,
        });
      });

      setCurrentPage(0);
      return;
    }

    if (currentPage === 2 && params.selectedMatriculation === 'COOPERATIVE') {
      setParams((previous) => {
        return ({
          ...previous,
          ...parameters,
        });
      });

      setCurrentPage(0);
      return;
    }

    if (currentPage === 1) {
      setParams((previous) => {
        return ({
          ...initialParameters(),
          serviceList: previous.serviceList,
        });
      });

      setCurrentPage(0);
      return;
    }

    if (currentPage === 2 && typeCurrentUser === 'COOPERCITRUS') {
      setParams((previous) => {
        return ({
          ...previous,
          ...parameters,
        });
      });

      setCurrentPage(0);
      return;
    }

    // default
    setParams((previous) => {
      return ({
        ...previous,
        ...parameters,
      });
    });
    setCurrentPage((previous) => previous - 1);
  };

  const nextPage = (parameters): void => {
    if (typeCurrentUser === 'COOPERCITRUS') {
      params.selectedMatriculation = null;
    }

    if (params.selectedMatriculation === null && currentPage === 0) {
      setParams((previous) => {
        return ({
          ...previous,
          ...parameters,
        });
      });

      setCurrentPage((previous) => previous + 1);
    }

    // condição quando o usuário adicionar uma nova propriedade.
    if (parameters?.createdLandId) {
      setParams((previous) => {
        return ({
          ...previous,
          isAddingLand: false,
          ...parameters,
        });
      });
      setCurrentPage(2);
      return;
    }

    if (currentPage === 2 && parameters.isAddingLand) {
      setParams((previous) => {
        return ({
          ...previous,
          ...parameters,
        });
      });
      setCurrentPage(-1);
      return;
    }

    // quando o serviço é pós-vendas não exige talhões para a criação do registro
    if (currentPage === 2 && params.selectedService.idCode === 'POS_VENDAS') {
      setParams((previous) => {
        return ({
          ...previous,
          ...parameters,
        });
      });

      setCurrentPage((previous) => previous + 2);
      return;
    }

    // condição quando o usuário clicar em Cancelar na tela de Adicionar propriedade.
    if (currentPage === -1) {
      setParams((previous) => {
        return ({
          ...previous,
          isAddingLand: false,
        });
      });

      setCurrentPage(1);
      return;
    }

    // pula 2 passos caso não tenha campos dinamicos e não seja do serviço 2 (geofert grid)
    if (
      currentPage === 3
      && !params.serviceTypeDynamicFields.length
      && !params.serviceSubtypeDynamicFields.length
      // && params.selectedType.id !== 3
    ) {
      setParams((previous) => {
        return ({
          ...previous,
          ...parameters,
        });
      });
      setCurrentPage((previous) => previous + 2);
      return;
    }

    // default
    setParams((previous) => {
      return ({
        ...previous,
        ...parameters,
      });
    });
    setCurrentPage((previous) => previous + 1);
  };

  const removeSelectedField = (id): void => {
    setParams((previous) => {
      return ({
        ...previous,
        talhoesSelecionados: params.talhoesSelecionados.filter((v) => v.id !== id),
      });
    });
  };

  const updateParams = (newParams): void => {
    setParams((previous) => {
      return ({
        ...previous,
        ...newParams,
      });
    });
  };
  const getDisplay = (_currentPage: number): string => {
    if (_currentPage === 0) {
      return 'block';
    }
    return 'none';
  };
  return (
    <div className="FlowService">
      <Spin tip="Carregando..." spinning={loading}>
        <div className="service-title">{getTitleText(currentPage)}</div>
        <div className="inner-container">
          <div style={{ display: getDisplay(currentPage) }}>
            {
              currentPage === 0
              && (
                <NewService
                  nextPage={nextPage}
                  params={params}
                />
              )
            }
          </div>
          <div style={{ display: currentPage === 1 ? 'block' : 'none' }}>
            {
              currentPage >= 1
              && (
                <ServiceEstimate
                  previousPage={previousPage}
                  nextPage={nextPage}
                  params={params}
                />
              )
            }
          </div>
          <div style={{ display: currentPage === 2 ? 'block' : 'none' }}>
            {
              currentPage >= 2
              && (
                <SelectPropertyData
                  previousPage={previousPage}
                  nextPage={nextPage}
                  params={params}
                  updateParams={updateParams}
                />
              )
            }
          </div>
          <div style={{ display: currentPage === 3 ? 'block' : 'none' }}>
            {
              currentPage === 3
              && (
                <GeoMap
                  previousPage={previousPage}
                  nextPage={nextPage}
                  params={params}
                />
              )
            }
          </div>
          <div style={{ display: currentPage === 4 ? 'block' : 'none' }}>
            {
              currentPage === 4
              && (
                <FillDetails
                  isCurrentPage={currentPage === 4 && params.selectedService.idCode !== 'POS_VENDAS'}
                  previousPage={previousPage}
                  nextPage={nextPage}
                  params={params}
                  removeSelectedField={removeSelectedField}
                />
              )
            }
          </div>
          <div style={{ display: currentPage === 5 ? 'block' : 'none' }}>
            {
              currentPage === 5
              && (
                <Summary
                  isCurrentPage={currentPage === 5 && params.selectedService.idCode !== 'POS_VENDAS'}
                  previousPage={previousPage}
                  nextPage={nextPage}
                  params={params}
                  removeSelectedField={removeSelectedField}
                />
              )
            }
          </div>
          <div style={{ display: currentPage === 6 ? 'block' : 'none' }}>
            {
              currentPage === 6
              && (
                <SuccessSolicitation
                  previousPage={previousPage}
                  params={params}
                />
              )
            }
          </div>
          <div style={{ display: currentPage === -1 ? 'block' : 'none' }}>
            {
              currentPage === -1
              && (
                <NewLand
                  nextPage={nextPage}
                  previousPage={previousPage}
                  params={params}
                />
              )
            }
          </div>
        </div>
      </Spin>
    </div>
  );
};

FlowService.propTypes = {};
FlowService.defaultProps = {};

export default FlowService;
